import { useCallback, useContext } from 'react'

import { Button } from 'ui/components/Buttons'
import { PageSection, PageSectionFace } from 'ui/components/Page'
import { AuthContext } from '~auth/AuthContext'
import { useSetDefaultDevice } from '~auth/hooks/mfa'

import { CreateDeviceDialog, DeviceTable } from './components'
import { useCreation } from './useCreation'
import { useDeletion } from './useDeletion'
import { useDeviceList } from './useDeviceList'

export const MFADevices = () => {
  const { portalName } = useContext(AuthContext)
  const { reloadDeviceList, dataSource, isFetching } = useDeviceList(portalName)
  const defaultDeviceMutation = useSetDefaultDevice(portalName)
  const { Confirmation, startDeleting } = useDeletion(portalName, reloadDeviceList)

  const {
    challengeStartedAt,
    totpUrl,
    resetChallenge,
    retryChallenge,
    sendSecurityCode,
    isCreationDialogOpen,
    openCreationDialog,
    closeCreationDialog,
    createDevice
  } = useCreation(portalName, reloadDeviceList)

  const setDefaultDevice = useCallback(
    async (id: number) => {
      await defaultDeviceMutation.mutateAsync({ id })
      reloadDeviceList()
    },
    [defaultDeviceMutation, reloadDeviceList]
  )

  return (
    <PageSection
      face={PageSectionFace.SECONDARY}
      title="MFA Devices"
      actions={
        <Button face="tertiary" onClick={openCreationDialog}>
          Add New
        </Button>
      }
    >
      <Confirmation />
      {isCreationDialogOpen && (
        <CreateDeviceDialog
          challengeStartedAt={challengeStartedAt}
          totpUrl={totpUrl}
          onClose={closeCreationDialog}
          onCreateDevice={createDevice}
          onResetChallenge={resetChallenge}
          onRetryChallenge={retryChallenge}
          onSendCode={sendSecurityCode}
        />
      )}
      <DeviceTable
        dataSource={dataSource}
        loading={isFetching}
        onDelete={startDeleting}
        onSetDefault={setDefaultDevice}
        portal={portalName}
      />
    </PageSection>
  )
}
